import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { controlsApi, Control } from '../api/controls.api';
import { useAuth } from '@contexts/AuthContext';

const ControlsLibrary: React.FC = () => {
  const { user } = useAuth();
  const [controls, setControls] = useState<Control[]>([]);
  const [stats, setStats] = useState<{ total: number; with_evidence: number; cross_framework_avg: number } | null>(null);
  const [selected, setSelected] = useState<Control | null>(null);
  const [crossFramework, setCrossFramework] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ name: '', control_ref: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const companyId = user?.company_id;

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes] = await Promise.all([
        controlsApi.list({ company_id: companyId, limit: 100 }),
        controlsApi.getStats(companyId),
      ]);
      setControls((list as any).controls || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleSelect = async (ctrl: Control) => {
    setSelected(ctrl);
    try {
      const cf = await controlsApi.getCrossFramework(ctrl.id);
      setCrossFramework(cf);
    } catch {
      setCrossFramework(null);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !companyId) return;
    setSubmitting(true);
    try {
      await controlsApi.create({ company_id: companyId, ...form });
      setCreateModal(false);
      setForm({ name: '', control_ref: '', description: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this control?')) return;
    try {
      await controlsApi.delete(id);
      if (selected?.id === id) setSelected(null);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Controls Library</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Add Control
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Controls', value: stats.total },
            { label: 'With Evidence', value: stats.with_evidence },
            { label: 'Avg Framework Coverage', value: `${(stats.cross_framework_avg || 0).toFixed(1)}` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-4">
        <div className="flex-1 bg-white border rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : controls.length === 0 ? (
            <div className="py-12 text-gray-500">No controls defined yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Ref', 'Name', 'Frameworks', 'Evidence', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y">
                {controls.map(ctrl => (
                  <tr key={ctrl.id} className={`hover:bg-gray-50 cursor-pointer ${selected?.id === ctrl.id ? 'bg-blue-50' : ''}`}
                    onClick={() => handleSelect(ctrl)}>
                    <td className="px-4 py-3 text-left font-mono text-xs text-blue-700 font-medium">{ctrl.control_ref}</td>
                    <td className="px-4 py-3 text-left font-medium text-gray-900">{ctrl.name}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{ctrl.framework_count ?? '-'}</td>
                    <td className="px-4 py-3 text-left text-gray-600">{ctrl.evidence_count ?? '-'}</td>
                    <td className="px-4 py-3 text-left">
                      <button onClick={e => { e.stopPropagation(); handleDelete(ctrl.id); }}
                        className="text-red-500 hover:underline text-xs">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="w-80 bg-white border rounded-xl p-4 space-y-4 shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{selected.name}</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{selected.control_ref}</span>
            </div>
            {selected.description && <p className="text-sm text-gray-600">{selected.description}</p>}

            {crossFramework && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Cross-Framework Coverage</h4>
                <div className="space-y-1">
                  {Array.isArray(crossFramework) && crossFramework.length > 0 ? crossFramework.map((f: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{f.framework_name || `Framework #${f.framework_id}`}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{f.page_count || 0} pages</span>
                    </div>
                  )) : (
                    <p className="text-xs text-gray-400">Not mapped to any framework pages yet.</p>
                  )}
                </div>
              </div>
            )}

            <div className="border-t pt-3 space-y-2">
              <p className="text-xs text-gray-500">To map evidence or pages, use the evidence upload or page management screens and reference this control.</p>
            </div>
          </div>
        )}
      </div>

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">Add Control</h2>
            <input placeholder="Control Reference (e.g. ISO-A.5.1) *" value={form.control_ref}
              onChange={e => setForm({ ...form, control_ref: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.name.trim() || !form.control_ref.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlsLibrary;
